<script setup>
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';

const { t } = useI18n();
const route = useRoute();
const currentAccountId = useMapGetter('getCurrentAccountId');

const url = computed(() => {
  const domain = window.location.origin;
  const accountId = currentAccountId.value;
  const automationId = route.params.automationId;
  return `${domain}/wgpt/webhooks/automations/${accountId}/${automationId}`;
});

const descriptionTooltip = {
  content: t(
    'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.TRIGGER.FORM.INBOUND_WEBHOOK.DESCRIPTION'
  ),
  popperClass:
    'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
  boundary: 'body',
  popperTriggers: ['hover'],
};

const copyToClipboard = () => {
  navigator.clipboard.writeText(url.value);
  useAlert(
    t(
      'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.TRIGGER.FORM.INBOUND_WEBHOOK.COPY_SUCCESSFUL'
    )
  );
};
</script>

<template>
  <div class="relative flex flex-col min-w-0 gap-1">
    <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
      <div class="block truncate">
        {{
          t(
            'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.TRIGGER.FORM.INBOUND_WEBHOOK.LABEL'
          )
        }}
      </div>
      <Icon
        v-tooltip.top="descriptionTooltip"
        icon="i-lucide-info"
        class="text-n-slate-11 size-3.5"
      />
    </label>

    <div class="flex relative">
      <input
        v-model.trim="url"
        class="input peer block w-full reset-base text-sm h-10 !px-3 !py-2.5 !mb-0 border rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out focus:!pe-10"
        type="text"
        readonly
      />

      <Button
        size="xs"
        color="slate"
        icon="i-lucide-copy"
        variant="faded"
        class="absolute right-0 top-0 m-2 invisible peer-focus:visible active:visible focus:visible"
        @click="copyToClipboard"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
input {
  @apply border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand;
}
</style>
